import React from 'react';
import {
    ClipboardCheck, ClipboardList, Download, FileText, FolderOpen, ListChecks, Mail, Plus, RefreshCw, Scale, Send,
} from 'lucide-react';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { readOnlyTitle } from '../../services/readOnlyNotice';
import { ADRESSAT_LABEL } from './fragenkatalog';
import { FolgeunterlagenKarte } from './FolgeunterlagenKarte';
import { FragenListe, AuswahlAktionen } from './FragenListe';
import { FreigabeKasten, FristenBanner, GegenstandslosBanner, HinweisListe } from './PlanungBausteine';
import { PlanungGrundlagen } from './PlanungGrundlagen';
import { PlanungKiLeiste } from './PlanungKiLeiste';
import { PlanungVorschlagsLeiste } from './PlanungVorschlagsLeiste';
import { UebergabeReiter } from './UebergabeReiter';
import { UnterlagenCheckliste } from './UnterlagenCheckliste';
import { SPEICHER_TON_KLASSE, speicherstand } from './autoSpeichern';
import { reiterWechselErlaubt, wechselSperre } from './leitfadenRahmen';
import type { PlanungAnsicht, PlanungReiterSchluessel } from './planungAnsicht';

/**
 * Die Ansicht „Alle Bereiche": sechs Reiter nebeneinander.
 *
 * Reine Darstellung — Zustand und Handler liegen in `PlanungModul`, hier
 * kommt alles ueber `a` (`PlanungAnsicht`) herein. Der Reiterzustand selbst
 * ist Prop, damit der Container ihn haelt. `meldungen` sind die Kaesten des
 * Containers (Fehler, Erfolg, wartender Druckauftrag); sie stehen zwischen
 * Kennzahlen und Fristen-Banner wie bisher.
 */
export const PlanungReiter: React.FC<{
    a: PlanungAnsicht;
    reiter: PlanungReiterSchluessel;
    onReiter: (r: PlanungReiterSchluessel) => void;
    meldungen: React.ReactNode;
}> = ({ a, reiter, onReiter, meldungen }) => {
    const speicher = speicherstand({
        schmutzig: a.schmutzig, abgeschlossen: a.abgeschlossen, isReadOnly: a.isReadOnly, hatPhase: !!a.phase,
        autoLaeuft: a.autoSpeichern.laeuft, fehlversuche: a.autoSpeichern.fehlversuche, zuletzt: a.autoSpeichern.zuletzt, gesperrt: a.laden || !!a.fehler,
    });
    const REITER: { key: PlanungReiterSchluessel; label: string; icon: React.ReactNode; zahl?: number; warn?: boolean }[] = [
        { key: 'grundlagen', label: 'Grundlagen', icon: <ClipboardList className="w-4 h-4" /> },
        { key: 'fragen', label: 'Fragenkatalog', icon: <FileText className="w-4 h-4" />, zahl: a.fragen.length },
        { key: 'unterlagen', label: 'Unterlagen', icon: <ClipboardCheck className="w-4 h-4" /> },
        { key: 'bedenken', label: 'Bedenken', icon: <Scale className="w-4 h-4" />, zahl: a.hinweise.length },
        // Die Zahl sind offene Fassungsfragen — gelb, weil sie auf eine
        // Entscheidung warten (Plan 6.1: „Später" → gelb in Ampel und Reiter).
        { key: 'uebergabe', label: 'Übergabe', icon: <ListChecks className="w-4 h-4" />, zahl: a.offeneFassungen, warn: true },
        { key: 'dokumente', label: 'Dokumente', icon: <FolderOpen className="w-4 h-4" /> },
    ];

    return (
        <>
            {/* ─── Kennzahlen ────────────────────────────────────────── */}
            {a.fragen.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                    {[
                        { wert: a.kennzahlen.gesamt, label: 'Fragen', farbe: 'text-slate-800' },
                        { wert: a.kennzahlen.beantwortet, label: 'beantwortet', farbe: 'text-emerald-600' },
                        { wert: a.kennzahlen.offenKritisch, label: 'A-Fragen offen', farbe: 'text-rose-600' },
                        { wert: a.kennzahlen.ueberfaellig, label: 'überfällig', farbe: 'text-rose-600' },
                        { wert: `${a.kennzahlen.quote} %`, label: 'Klärungsstand', farbe: 'text-blue-600' },
                    ].map(k => (
                        <div key={k.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
                            <div className={`text-2xl font-black ${k.farbe}`}>{k.wert}</div>
                            <div className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">{k.label}</div>
                        </div>
                    ))}
                </div>
            )}

            {meldungen}

            <FristenBanner {...a.fristenBanner} onAnsehen={() => onReiter('fragen')} />
            <GegenstandslosBanner {...a.gegenstandslosBanner} />

            {/* ─── Reiter ────────────────────────────────────────────── */}
            {/* `flex-wrap` statt `overflow-x-auto`: bei vier Reitern brachte die
                waagerechte Bildlaufleiste nichts ausser einem Balken quer
                unter der Ueberschrift. Sie umbrechen zu lassen kostet auf
                schmalen Fenstern eine Zeile und sonst nichts. */}
            <div data-glass="reiter" className="flex items-center gap-1 border-b border-slate-200 mb-6 flex-wrap">
                {REITER.map(r => (
                    <button
                        key={r.key}
                        type="button"
                        onClick={() => onReiter(r.key)}
                        // Waehrend die KI liest, haengte jeder andere Reiter die
                        // KI-Leiste aus und braeche den Lauf ab.
                        disabled={!reiterWechselErlaubt(a.kiLaeuft, r.key)}
                        title={reiterWechselErlaubt(a.kiLaeuft, r.key) ? undefined : (wechselSperre(a.kiLaeuft) ?? undefined)}
                        className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                            reiter === r.key
                                ? 'border-blue-600 text-blue-600'
                                : 'border-transparent text-slate-500 hover:text-slate-700'
                        }`}
                    >
                        {r.icon} {r.label}
                        {typeof r.zahl === 'number' && r.zahl > 0 && (
                            <span className={`text-[10px] font-black rounded-full px-1.5 py-0.5 ${
                                r.warn ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500'
                            }`}>
                                {r.zahl}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {a.laden && <p className="text-sm text-slate-400 py-10 text-center">Wird geladen …</p>}

            {/* ─── Grundlagen ────────────────────────────────────────── */}
            {!a.laden && reiter === 'grundlagen' && (
                <>
                    <PlanungVorschlagsLeiste {...a.vorschlagsLeiste} />
                    <PlanungGrundlagen
                        {...a.grundlagen}
                        nachBeschreibung={<PlanungKiLeiste {...a.kiLeiste} />}
                    />

                    <div data-glass="fussleiste" className="sticky bottom-0 mt-6 bg-white/95 backdrop-blur border-t border-slate-200 -mx-4 px-4 py-3 flex items-center gap-3 flex-wrap">
                        {/* Die Grundlagen speichern sich selbst (`autoSpeichern.ts`);
                            der Knopf erscheint nur nach einem Fehlschlag. */}
                        <span role="status" className={`text-xs font-bold ${SPEICHER_TON_KLASSE[speicher.ton]}`}>
                            {speicher.text}
                        </span>
                        {speicher.erneut && (
                            <button
                                type="button"
                                onClick={() => { void a.speichereGrundlagen(); }}
                                disabled={a.isReadOnly || a.abgeschlossen || a.laeuft || !a.schmutzig}
                                title={a.isReadOnly ? readOnlyTitle('Speichern') : undefined}
                                className="px-4 py-2 text-sm font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                            >
                                Erneut versuchen
                            </button>
                        )}
                        {a.fragen.length === 0 ? (
                            <button
                                type="button"
                                onClick={() => a.erzeugeKatalog()}
                                disabled={a.isReadOnly || a.laeuft}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                            >
                                <Plus className="w-4 h-4" /> Fragenkatalog erzeugen
                            </button>
                        ) : a.nachziehbar.length > 0 ? (
                            <button
                                type="button"
                                onClick={() => a.zieheNach()}
                                disabled={a.isReadOnly || a.abgeschlossen || a.laeuft}
                                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-blue-600 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-40"
                            >
                                <RefreshCw className="w-4 h-4" /> {a.nachziehbar.length} neue Frage(n) nachziehen
                            </button>
                        ) : (
                            <span className="text-xs text-slate-400">
                                Der Katalog ist auf dem Stand Ihrer Angaben.
                            </span>
                        )}
                    </div>
                </>
            )}

            {/* ─── Fragenkatalog ─────────────────────────────────────── */}
            {!a.laden && reiter === 'fragen' && (
                <div className="space-y-4">
                    {a.fragen.length === 0 ? (
                        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                            <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                            <p className="text-sm text-slate-500 mb-4">
                                Noch kein Fragenkatalog. Erfassen Sie zuerst die Baubeschreibung und die
                                Projektmerkmale — daraus entsteht der auf dieses Vorhaben zugeschnittene Katalog.
                            </p>
                            <button
                                type="button"
                                onClick={() => a.zuGrundlagen()}
                                className="px-4 py-2 text-sm font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                            >
                                Zu den Grundlagen
                            </button>
                        </div>
                    ) : (
                        <>
                            <AuswahlAktionen {...a.auswahlAktionen} />

                            <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center gap-3 flex-wrap">
                                <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Klärungsliste erzeugen</span>
                                <button
                                    type="button"
                                    onClick={() => a.exportiere(null, true)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
                                >
                                    <Download className="w-3.5 h-3.5" /> Interner Stand (mit Antworten)
                                </button>
                                {/* Je Adressat zwei Wege: PDF speichern (wie bisher) und per
                                    E-Mail versenden (Stufe 4.1) — der Versand stempelt, das
                                    Speichern nicht. */}
                                {a.adressaten.map(ad => (
                                    <span key={ad.adressat} className="inline-flex items-stretch rounded-lg border border-blue-200 overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => a.exportiere(ad.adressat, false)}
                                            disabled={!ad.offen}
                                            title={`${ADRESSAT_LABEL[ad.adressat]} — PDF speichern`}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 disabled:opacity-40"
                                        >
                                            <Download className="w-3.5 h-3.5" /> {ad.adressat} ({ad.offen})
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => a.oeffneVersand(ad.adressat)}
                                            disabled={!ad.offen || a.isReadOnly || a.abgeschlossen || a.laeuft}
                                            title={a.isReadOnly ? readOnlyTitle('Versenden') : `${ADRESSAT_LABEL[ad.adressat]} — per E-Mail versenden`}
                                            aria-label={`Klärungsliste an ${ADRESSAT_LABEL[ad.adressat]} versenden`}
                                            className="inline-flex items-center px-2 text-blue-600 bg-blue-50 hover:bg-blue-100 border-l border-blue-200 disabled:opacity-40"
                                        >
                                            <Send className="w-3.5 h-3.5" />
                                        </button>
                                    </span>
                                ))}
                                {/* Stufe 7.2: Antworten aus einer Mail den offenen Fragen
                                    zuordnen lassen — nur Vorschlaege, den Status setzt der Mensch. */}
                                <button
                                    type="button"
                                    onClick={() => a.oeffneAntwortMail()}
                                    disabled={!a.antwortMailMoeglich}
                                    title={a.isReadOnly
                                        ? readOnlyTitle('Antwortmail einfügen')
                                        : 'Text einer Antwortmail einfügen — die KI ordnet die Antworten den offenen Fragen zu'}
                                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 disabled:opacity-40"
                                >
                                    <Mail className="w-3.5 h-3.5" /> Antwortmail einfügen (KI)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => a.neueEigeneFrage()}
                                    disabled={a.isReadOnly || a.abgeschlossen || a.laeuft}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Eigene Frage
                                </button>
                            </div>

                            <FragenListe {...a.fragenListe} />

                            {/* ─── Freigabe-Gate ─────────────────────── */}
                            <FreigabeKasten {...a.freigabeKasten} />
                        </>
                    )}
                </div>
            )}

            {/* ─── Unterlagen ────────────────────────────────────────── */}
            {!a.laden && reiter === 'unterlagen' && (
                <UnterlagenCheckliste {...a.unterlagenCheckliste} />
            )}

            {/* ─── Bedenken ──────────────────────────────────────────── */}
            {!a.laden && reiter === 'bedenken' && (
                <HinweisListe {...a.hinweisListe} />
            )}

            {/* ─── Übergabe (Stufe 6.2, nur lesend) ──────────────────── */}
            {/* Darueber die Folgeunterlagen: Entwuerfe aus der Planung anlegen. */}
            {!a.laden && reiter === 'uebergabe' && (
                <div className="space-y-4">
                    <FolgeunterlagenKarte {...a.folgeunterlagen} />
                    <UebergabeReiter {...a.uebergabe} />
                </div>
            )}

            {/* ─── Dokumente ─────────────────────────────────────────── */}
            {!a.laden && reiter === 'dokumente' && (
                <DokumentePanel {...a.dokumente} />
            )}
        </>
    );
};
